import React from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";

// Helper function to properly format currency with 2 decimal places
const formatCurrency = (amount) => {
  return parseFloat(amount || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// Fallback function to generate PDF using jsPDF text (no HTML rendering)
export const generatePDFText = (invoice, user, cartList, type) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.width;
  const pageHeight = pdf.internal.pageSize.height;
  const rightMargin = 30; // Increased right margin
  let yPos = 20;

  // Business info
  const businessName = user.business_name || (user.employer && user.employer.business_name) || 'Business';
  const title = type === "receipt" ? "RECEIPT" : "INVOICE";
  const documentId = invoice.number || 'N/A';

  // Check for business logo
  const logoUrl = user.image || (user.employer && user.employer.image);
  let logoStartX = 20;
  let logoWidth = 0;
  
  // Function to load and add logo
  const addLogo = async () => {
    if (logoUrl) {
      try {
        logoWidth = 40;
        const logoHeight = 30;
        
        // Try to load the actual image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            try {
              // Create canvas to convert image to base64
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = img.width;
              canvas.height = img.height;
              ctx.drawImage(img, 0, 0);
              
              const imgData = canvas.toDataURL('image/png');
              pdf.addImage(imgData, 'PNG', logoStartX, yPos, logoWidth, logoHeight);
              resolve();
            } catch (error) {
              console.log("Could not process logo image:", error);
              // Add placeholder
              addLogoPlaceholder();
              resolve();
            }
          };
          
          img.onerror = () => {
            console.log("Could not load logo from URL");
            addLogoPlaceholder();
            resolve();
          };
          
          // Set timeout for image loading
          setTimeout(() => {
            console.log("Logo loading timeout");
            addLogoPlaceholder();
            resolve();
          }, 2000);
        });
        
        img.src = logoUrl;
        yPos += logoHeight + 10;
      } catch (error) {
        console.log("Error loading logo:", error);
        addLogoPlaceholder();
      }
    }
  };

  // Function to add logo placeholder
  const addLogoPlaceholder = () => {
    if (logoUrl) {
      logoWidth = 40;
      const logoHeight = 30;
      pdf.setDrawColor(200);
      pdf.rect(logoStartX, yPos, logoWidth, logoHeight);
      pdf.setFontSize(8);
      pdf.text('LOGO', logoStartX + 15, yPos + 18);
      yPos += logoHeight + 10;
    }
  };

  // For now, just add placeholder (async logo loading would complicate the current structure)
  addLogoPlaceholder();

  // Header - adjust position if logo exists
  const headerStartX = logoUrl ? logoStartX + logoWidth + 10 : logoStartX;
  pdf.setFontSize(24);
  pdf.text(title, headerStartX, logoUrl ? 30 : yPos);
  pdf.setFontSize(12);
  const docNumY = logoUrl ? 40 : yPos + 10;
  pdf.text(`# ${documentId}`, headerStartX, docNumY);
  
  // Business info (right aligned with margin)
  let businessInfoY = logoUrl ? 20 : 20;
  pdf.setFontSize(14);
  pdf.text(businessName, pageWidth - rightMargin, businessInfoY, { align: 'right' });
  businessInfoY += 7;
  if (user.address_line1) {
    pdf.setFontSize(10);
    pdf.text(user.address_line1, pageWidth - rightMargin, businessInfoY, { align: 'right' });
    businessInfoY += 5;
  }
  if (user.address_line2) {
    pdf.text(user.address_line2, pageWidth - rightMargin, businessInfoY, { align: 'right' });
    businessInfoY += 5;
  }
  if (user.contact_number) {
    pdf.text(`Tel: ${user.contact_number}`, pageWidth - rightMargin, businessInfoY, { align: 'right' });
    businessInfoY += 5;
  }
  if (user.email) {
    pdf.text(`Email: ${user.email}`, pageWidth - rightMargin, businessInfoY, { align: 'right' });
  }

  yPos = Math.max(yPos, businessInfoY + 20);
  
  // Customer info and Invoice details side by side
  const customerName = invoice.customer ? 
    `${invoice.customer.first_name || ''} ${invoice.customer.last_name || ''}`.trim() :
    `${invoice.first_name || ''} ${invoice.last_name || ''}`.trim() || 'Customer';
  
  // Left side - Customer info
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Bill To:', 20, yPos);
  pdf.setFont('helvetica', 'normal');
  yPos += 7;
  pdf.text(customerName, 20, yPos);
  
  if (invoice.customer && invoice.customer.email) {
    yPos += 5;
    pdf.text(invoice.customer.email, 20, yPos);
  }
  if (invoice.customer && invoice.customer.phone_number) {
    yPos += 5;
    pdf.text(invoice.customer.phone_number, 20, yPos);
  }

  // Right side - Invoice details
  const invoiceDetailsY = yPos - 12; // Reset to same level as "Bill To"
  const invoiceDate = invoice.created_at ? moment(invoice.created_at).format("MMM DD, YYYY") : moment().format("MMM DD, YYYY");
  
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${title} Date:`, 110, invoiceDetailsY);
  pdf.setFont('helvetica', 'normal');
  pdf.text(invoiceDate, pageWidth - rightMargin - 10, invoiceDetailsY, { align: 'right' });
  
  if (type !== "receipt" && invoice.due_date) {
    const dueDate = moment(invoice.due_date).format("MMM DD, YYYY");
    pdf.setFont('helvetica', 'bold');
    pdf.text('Due Date:', 110, invoiceDetailsY + 7);
    pdf.setFont('helvetica', 'normal');
    pdf.text(dueDate, pageWidth - rightMargin - 10, invoiceDetailsY + 7, { align: 'right' });
  }

  yPos += 25;

  // Items header
  pdf.setFontSize(10);
  pdf.text('Item', 20, yPos);
  pdf.text('Description', 70, yPos);
  pdf.text('Qty', 115, yPos);
  pdf.text('Price', 135, yPos);
  pdf.text('Total', pageWidth - rightMargin - 10, yPos, { align: 'right' });
  pdf.line(20, yPos + 2, pageWidth - rightMargin, yPos + 2);
  yPos += 10;

  // Items
  let subtotal = 0;
  let totalTax = 0;
  cartList.forEach(item => {
    // Get correct price - check for variant price first, then selling_price, then price
    let price = 0;
    let isVariantItem = false;
    if (item.variants && item.variants.length > 0 && item.variants[0].price) {
      price = parseFloat(item.variants[0].price);
      isVariantItem = true;
    } else {
      price = parseFloat(item.selling_price || item.price || 0);
    }
    
    const quantity = parseInt(item.quantity || 1);
    let lineSubtotal = price * quantity;
    
    // Apply tax logic based on item type
    let lineTax = 0;
    let lineTotal = lineSubtotal;
    
    if (item.tax_rate && parseFloat(item.tax_rate) > 0) {
      if (isVariantItem) {
        // Variant items: price is tax-exclusive, add tax
        lineTax = (parseFloat(item.tax_rate) / 100) * lineSubtotal;
        lineTotal = lineSubtotal + lineTax;
        totalTax += lineTax;
      } else {
        // Non-variant items: price is already tax-inclusive, extract tax
        const taxRate = parseFloat(item.tax_rate);
        lineTax = (lineSubtotal * taxRate) / (100 + taxRate);
        lineTotal = lineSubtotal; // Price already includes tax
        totalTax += lineTax;
        lineSubtotal = lineSubtotal - lineTax; // Extract net amount for subtotal
      }
    }
    
    subtotal += lineSubtotal;
    
    const itemName = item.name || item.product_name || 'Item';
    const itemDescription = item.description || item.product_description || '';
    
    // Variant info if exists
    let variantInfo = '';
    if (item.variants && item.variants.length > 0) {
      variantInfo = item.variants.map(variant => `${variant.type}: ${variant.value}`).join(', ');
    }
    
    // Brand Color Loystar
    const loystarColor = [217, 0, 104]; // #D90068 in RGB

    // Item name (bold)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    
    // Split long item name into multiple lines if needed
    const nameLines = pdf.splitTextToSize(itemName, 45);
    pdf.text(nameLines, 20, yPos);
    
    const nameHeight = (nameLines.length - 1) * 5;

    // Show variant info on next line if exists
    if (variantInfo) {
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(loystarColor[0], loystarColor[1], loystarColor[2]);
      pdf.text(variantInfo, 20, yPos + 5 + nameHeight);
    }
    
    // Description (normal font) - no truncation
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    const descLines = pdf.splitTextToSize(itemDescription, 40);
    pdf.text(descLines, 70, yPos);
    
    const descHeight = (descLines.length - 1) * 4;

    // Quantity, Price, Total - better spacing
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text(quantity.toString(), 115, yPos, { align: 'center' });
    pdf.text(parseFloat(lineTotal / quantity).toFixed(2), 135, yPos);
    pdf.text(parseFloat(lineTotal).toFixed(2), pageWidth - rightMargin - 10, yPos, { align: 'right' });
    
    // Add extra space based on content height
    const rowHeight = Math.max(7 + nameHeight + (variantInfo ? 5 : 0), 7 + descHeight);
    yPos += rowHeight + 2;
  });

  // Totals
  yPos += 10;
  pdf.line(110, yPos, pageWidth - rightMargin, yPos);
  yPos += 7;
  
  const discountAmount = parseFloat(invoice.discount_amount || 0);
  const totalAmount = subtotal + totalTax - discountAmount;
  const paidAmount = parseFloat(invoice.paid_amount || 0);
  const dueAmount = totalAmount - paidAmount;
  const totalVATAmount = totalTax; // Add this for HTML template compatibility

  // Helper function to format currency with 2 decimal places
  const formatCurrency = (amount) => `${user.currency || ''}${parseFloat(amount).toFixed(2)}`;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.text('Subtotal:', 110, yPos);
  pdf.text(formatCurrency(subtotal), pageWidth - rightMargin - 10, yPos, { align: 'right' });
  yPos += 7;

  if (totalTax > 0) {
    pdf.text('VAT/Tax:', 110, yPos);
    pdf.text(formatCurrency(totalTax), pageWidth - rightMargin - 10, yPos, { align: 'right' });
    yPos += 7;
  }

  if (discountAmount > 0) {
    pdf.text('Discount:', 110, yPos);
    pdf.text(`-${formatCurrency(discountAmount)}`, pageWidth - rightMargin - 10, yPos, { align: 'right' });
    yPos += 7;
  }

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Total:', 110, yPos);
  pdf.text(formatCurrency(totalAmount), pageWidth - rightMargin - 10, yPos, { align: 'right' });
  yPos += 10;

  if (paidAmount > 0) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Amount Paid:', 110, yPos);
    pdf.text(formatCurrency(paidAmount), pageWidth - rightMargin - 10, yPos, { align: 'right' });
    yPos += 7;
  }

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Amount Due:', 110, yPos);
  pdf.text(formatCurrency(dueAmount), pageWidth - rightMargin - 10, yPos, { align: 'right' });
  yPos += 20;

  // Payment Message/Note
  if (invoice.payment_message && invoice.payment_message.trim()) {
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Payment Note:', 20, yPos);
    yPos += 8;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    
    // Split long payment message into multiple lines
    const maxLineWidth = pageWidth - rightMargin - 40; // Leave margins
    const lines = pdf.splitTextToSize(invoice.payment_message, maxLineWidth);
    
    // Add each line
    lines.forEach(line => {
      pdf.text(line, 20, yPos);
      yPos += 6;
    });
    
    yPos += 10; // Add some space after payment message
  }

  // Footer - "Powered by Loystar"
  const footerY = pageHeight - 20; // Position near bottom of page
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(128, 128, 128); // Gray color
  pdf.text('Powered by Loystar', pageWidth / 2, footerY, { align: 'center' });

  // Download
  pdf.save(`${title.toLowerCase()}-${documentId}.pdf`);
};

// Function to generate and download PDF
export const generatePDF = async (invoice, user, cartList, type) => {
  console.log("PDF Generation - Invoice:", invoice);
  console.log("PDF Generation - User:", user);
  console.log("PDF Generation - CartList:", cartList);
  console.log("PDF Generation - Type:", type);

  // Handle business information from user or employer
  const businessName = user.business_name || (user.employer && user.employer.business_name) || 'Business Name';
  const businessAddress1 = user.address_line1 || (user.employer && user.employer.address_line1) || '';
  const businessAddress2 = user.address_line2 || (user.employer && user.employer.address_line2) || '';
  const businessContact = user.contact_number || (user.employer && user.employer.contact_number) || '';
  const businessEmail = user.email || (user.employer && user.employer.email) || '';
  const logoUrl = user.image || (user.employer && user.employer.image);
  
  const isReceipt = type === "receipt";
  const title = isReceipt ? "RECEIPT" : "INVOICE";
  const documentId = invoice.number || 'N/A';
  
  // Brand color
  const loystarColor = "#D90068";
  
  // Customer information
  const customerName = invoice.customer ? 
    `${invoice.customer.first_name || ''} ${invoice.customer.last_name || ''}`.trim() :
    `${invoice.first_name || ''} ${invoice.last_name || ''}`.trim() || 'Customer';
  const customerEmail = invoice.customer ? invoice.customer.email : invoice.email || '';
  const customerPhone = invoice.customer ? invoice.customer.phone_number : invoice.phone_number || '';
  const customerAddress = invoice.customer_address || '';
  
  // Calculate totals
  const subtotal = cartList.reduce((sum, item) => {
    const price = parseFloat(
      (item.variants && item.variants.length > 0 && item.variants[0].price) 
        ? item.variants[0].price 
        : (item.selling_price || item.price || 0)
    );
    const quantity = parseInt(item.quantity || 1);
    const lineSubtotal = price * quantity;
    const isVariantItem = item.variants && item.variants.length > 0 && item.variants[0].price;
    
    if (item.tax_rate && parseFloat(item.tax_rate) > 0 && !isVariantItem) {
      // Non-variant items: price is tax-inclusive, extract net amount
      const taxRate = parseFloat(item.tax_rate);
      const netAmount = (lineSubtotal * 100) / (100 + taxRate);
      return sum + netAmount;
    } else {
      // Variant items or non-taxed items: use full amount as subtotal
      return sum + lineSubtotal;
    }
  }, 0);
  
  const totalVATAmount = cartList.reduce((sum, item) => {
    const price = parseFloat(
      (item.variants && item.variants.length > 0 && item.variants[0].price) 
        ? item.variants[0].price 
        : (item.selling_price || item.price || 0)
    );
    const quantity = parseInt(item.quantity || 1);
    const taxRate = parseFloat(item.tax_rate || 0);
    const lineSubtotal = price * quantity;
    const isVariantItem = item.variants && item.variants.length > 0 && item.variants[0].price;
    
    if (taxRate > 0) {
      if (isVariantItem) {
        // Variant items: price is tax-exclusive, calculate tax on subtotal
        const taxAmount = (lineSubtotal * taxRate / 100);
        return sum + taxAmount;
      } else {
        // Non-variant items: price is tax-inclusive, extract tax
        const taxAmount = (lineSubtotal * taxRate) / (100 + taxRate);
        return sum + taxAmount;
      }
    }
    return sum;
  }, 0);
  
  const discountAmount = invoice.discount_amount || 0;
  const totalAmount = subtotal + totalVATAmount - discountAmount;
  const paidAmount = invoice.paid_amount || 0;
  const dueAmount = totalAmount - paidAmount;
  
  // Format dates
  const invoiceDate = invoice.created_at ? moment(invoice.created_at).format("MMMM Do, YYYY") : moment().format("MMMM Do, YYYY");
  const dueDate = invoice.due_date ? moment(invoice.due_date).format("MMMM Do, YYYY") : '';
  
  // Create a temporary div to render the content
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = `
    <div style="
      padding: 40px; 
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; 
      width: 800px; 
      background: white; 
      color: #333;
      box-sizing: border-box;
      font-size: 14px;
      line-height: 1.6;
    ">
      <!-- Top Banner Style -->
      <div style="height: 5px; background: ${loystarColor}; margin: -40px -40px 30px -40px;"></div>

      <!-- Header -->
      <table style="width: 100%; margin-bottom: 40px;">
        <tr>
          <td style="vertical-align: top; width: 60%;">
            ${logoUrl ? `
              <img src="${logoUrl}" style="max-height: 80px; width: auto; margin-bottom: 20px; object-fit: contain; display: block;" onerror="this.style.display='none'"/>
            ` : ''}
            <h1 style="margin: 0; font-size: 38px; color: ${loystarColor}; font-weight: 800; letter-spacing: -1px;">${title}</h1>
            <p style="margin: 5px 0; color: #888; font-size: 16px; font-weight: 500;"># ${documentId}</p>
          </td>
          <td style="text-align: right; vertical-align: top; width: 40%;">
            <div style="font-size: 20px; font-weight: 700; color: #111; margin-bottom: 10px;">${businessName}</div>
            <div style="color: #666; font-size: 13px; line-height: 1.5;">
              ${businessAddress1 ? businessAddress1 + '<br/>' : ''}
              ${businessAddress2 ? businessAddress2 + '<br/>' : ''}
              ${businessContact ? '<b>Tel:</b> ' + businessContact + '<br/>' : ''}
              ${businessEmail ? '<b>Email:</b> ' + businessEmail : ''}
            </div>
          </td>
        </tr>
      </table>

      <!-- Customer and Invoice Info -->
      <table style="width: 100%; margin-bottom: 40px;">
        <tr>
          <td style="vertical-align: top; width: 50%;">
            <div style="text-transform: uppercase; font-size: 11px; font-weight: 700; color: #999; margin-bottom: 8px; letter-spacing: 1px;">Bill To:</div>
            <div style="line-height: 1.6;">
              <div style="font-size: 18px; font-weight: 700; color: #111;">${customerName}</div>
              <div style="color: #666;">
                ${customerEmail ? customerEmail + '<br/>' : ''}
                ${customerPhone ? customerPhone + '<br/>' : ''}
                ${customerAddress ? customerAddress : ''}
              </div>
            </div>
          </td>
          <td style="text-align: right; vertical-align: top; width: 50%;">
            <div style="margin-bottom: 6px; display: flex; justify-content: flex-end;">
              <span style="color: #999; font-weight: 600; text-transform: uppercase; font-size: 11px; margin-right: 15px; margin-top: 3px;">Date:</span>
              <span style="font-weight: 600; color: #333;">${invoiceDate}</span>
            </div>
            ${!isReceipt && dueDate ? `
              <div style="margin-bottom: 15px; display: flex; justify-content: flex-end;">
                <span style="color: #999; font-weight: 600; text-transform: uppercase; font-size: 11px; margin-right: 15px; margin-top: 3px;">Due Date:</span>
                <span style="font-weight: 600; color: #333;">${dueDate}</span>
              </div>
            ` : ''}
            
            <div style="margin-top: 20px; border: 1px solid #eee; padding: 15px; border-radius: 8px; display: inline-block; min-width: 200px;">
              <div style="text-transform: uppercase; font-size: 10px; font-weight: 800; color: #999; margin-bottom: 5px; text-align: center;">
                Balance ${invoice.status === 'paid' ? 'PAID' : 'DUE'}
              </div>
              <div style="font-size: 24px; font-weight: 800; color: ${invoice.status === 'paid' ? '#2ecc71' : loystarColor}; text-align: center;">
                ${user.currency || ''}${formatCurrency(dueAmount)}
              </div>
            </div>
          </td>
        </tr>
      </table>

      <!-- Items Table -->
      <table style="width: 100%; border-collapse: separate; border-spacing: 0; margin-bottom: 40px;">
        <thead>
          <tr>
            <th style="background-color: ${loystarColor}; color: white; padding: 15px; text-align: left; font-weight: 700; border-top-left-radius: 8px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Item & Description</th>
            <th style="background-color: ${loystarColor}; color: white; padding: 15px; text-align: center; font-weight: 700; width: 80px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Qty</th>
            <th style="background-color: ${loystarColor}; color: white; padding: 15px; text-align: right; font-weight: 700; width: 110px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Rate</th>
            <th style="background-color: ${loystarColor}; color: white; padding: 15px; text-align: right; font-weight: 700; width: 130px; border-top-right-radius: 8px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${cartList.map((item, index) => {
            const price = parseFloat(
              (item.variants && item.variants.length > 0 && item.variants[0].price) 
                ? item.variants[0].price 
                : (item.selling_price || item.price || 0)
            );
            const quantity = parseInt(item.quantity || 1);
            const taxRate = parseFloat(item.tax_rate || 0);
            const lineSubtotal = price * quantity;
            const isVariantItem = item.variants && item.variants.length > 0 && item.variants[0].price;
            
            let amount = lineSubtotal;
            if (taxRate > 0) {
              if (isVariantItem) {
                amount = lineSubtotal + (lineSubtotal * taxRate / 100);
              } else {
                amount = lineSubtotal;
              }
            }
            
            const itemName = item.name || item.product_name || 'Item';
            const itemDescription = item.description || item.product_description || '';
            const variantName = item.variants && item.variants.length > 0 && item.variants[0].value ? item.variants[0].value : (item.variants && item.variants.length > 0 && item.variants[0].name ? item.variants[0].name : '');
            
            return `
            <tr style="background-color: ${index % 2 === 0 ? '#fff' : '#fcfcfc'};">
              <td style="padding: 18px 15px; border-bottom: 1px solid #efefef; vertical-align: top;">
                <div style="font-weight: 700; color: #111; margin-bottom: 4px;">${itemName}</div>
                ${itemDescription ? `<div style="font-size: 12px; color: #777; line-height: 1.4; margin-bottom: 4px;">${itemDescription}</div>` : ''}
                ${variantName ? `<div style="font-size: 11px; color: ${loystarColor}; font-weight: 600;">Variant: ${variantName}</div>` : ''}
                ${taxRate > 0 ? `<div style="font-size: 11px; color: #999;">Tax: ${taxRate}%${isVariantItem ? ' (excl.)' : ' (incl.)'}</div>` : ''}
              </td>
              <td style="padding: 18px 15px; border-bottom: 1px solid #efefef; text-align: center; vertical-align: top; font-weight: 600;">${quantity}</td>
              <td style="padding: 18px 15px; border-bottom: 1px solid #efefef; text-align: right; vertical-align: top; color: #555;">
                ${formatCurrency(price)}
              </td>
              <td style="padding: 18px 15px; border-bottom: 1px solid #efefef; text-align: right; vertical-align: top; font-weight: 700; color: #111;">
                ${formatCurrency(amount)}
              </td>
            </tr>
            `;
          }).join('')}
        </tbody>
      </table>

      <!-- Totals Section -->
      <div style="display: flex; justify-content: flex-end;">
        <div style="width: 320px;">
          <table style="width: 100%; border-collapse: collapse;">
            <tr>
              <td style="padding: 8px 0; color: #888; font-weight: 600;">Subtotal:</td>
              <td style="padding: 8px 0; text-align: right; font-weight: 700; color: #333;">${user.currency || ''}${formatCurrency(subtotal)}</td>
            </tr>
            ${totalVATAmount > 0 ? `
              <tr>
                <td style="padding: 8px 0; color: #888; font-weight: 600;">VAT / Tax:</td>
                <td style="padding: 8px 0; text-align: right; font-weight: 700; color: #333;">${user.currency || ''}${formatCurrency(totalVATAmount)}</td>
              </tr>
            ` : ''}
            ${discountAmount > 0 ? `
              <tr style="color: #e74c3c;">
                <td style="padding: 8px 0; font-weight: 600;">Discount:</td>
                <td style="padding: 8px 0; text-align: right; font-weight: 700;">-${user.currency || ''}${formatCurrency(discountAmount)}</td>
              </tr>
            ` : ''}
            <tr style="border-top: 2px solid #333;">
              <td style="padding: 15px 0; font-size: 18px; font-weight: 800; color: #111;">Total:</td>
              <td style="padding: 15px 0; text-align: right; font-size: 22px; font-weight: 800; color: ${loystarColor};">${user.currency || ''}${formatCurrency(totalAmount)}</td>
            </tr>
            ${paidAmount > 0 ? `
              <tr>
                <td style="padding: 8px 0; color: #2ecc71; font-weight: 600;">Amount Paid:</td>
                <td style="padding: 8px 0; text-align: right; font-weight: 700; color: #2ecc71;">${user.currency || ''}${formatCurrency(paidAmount)}</td>
              </tr>
            ` : ''}
            <tr style="background-color: #fcfcfc;">
              <td style="padding: 15px; font-weight: 800; font-size: 16px; border-radius: 8px 0 0 8px;">Amount Due:</td>
              <td style="padding: 15px; text-align: right; font-weight: 800; color: ${dueAmount > 0 ? loystarColor : '#2ecc71'}; font-size: 18px; border-radius: 0 8px 8px 0;">
                ${user.currency || ''}${formatCurrency(dueAmount)}
              </td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Extra Notes Section -->
      ${(invoice.payment_message || (!isReceipt && user.bank_account_number && dueAmount > 0)) ? `
        <div style="margin-top: 50px; display: grid; grid-template-columns: 1fr 1fr; gap: 30px;">
          ${invoice.payment_message ? `
            <div style="padding: 20px; background: #fdf2f6; border-radius: 12px; border: 1px solid #f9e2ec;">
              <h4 style="margin: 0 0 10px 0; color: ${loystarColor}; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">Merchant's Note</h4>
              <p style="margin: 0; color: #555; font-size: 13px; line-height: 1.6;">${invoice.payment_message}</p>
            </div>
          ` : '<div></div>'}

          ${!isReceipt && user.bank_account_number && dueAmount > 0 ? `
            <div style="padding: 20px; background: #f4f8fb; border-radius: 12px; border: 1px solid #e1e9f1;">
              <h4 style="margin: 0 0 12px 0; color: #2c3e50; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">Bank Details</h4>
              <div style="font-size: 13px; color: #555;">
                <div style="margin-bottom: 5px;"><b>Account Number:</b> ${user.bank_account_number}</div>
                ${user.bank_name ? `<div><b>Bank Name:</b> ${user.bank_name}</div>` : ''}
              </div>
            </div>
          ` : ''}
        </div>
      ` : ''}

      <!-- Footer -->
      <div style="text-align: center; margin-top: 80px; padding-top: 30px; border-top: 1px solid #eee;">
        <div style="font-size: 16px; font-weight: 700; color: #333; margin-bottom: 5px;">Thank you for your business!</div>
        <div style="color: #999; font-size: 12px; font-weight: 500;">
          If you have any questions about this ${title.toLowerCase()}, please contact us.
        </div>
        <div style="margin-top: 30px; display: flex; justify-content: center; align-items: center; gap: 10px;">
          <span style="font-size: 11px; color: #ccc; text-transform: uppercase; letter-spacing: 2px;">Powered by</span>
          <span style="font-weight: 800; color: ${loystarColor}; font-size: 14px; letter-spacing: -0.5px;">Loystar</span>
        </div>
      </div>
    </div>
  `;

  // Add to document temporarily
  // Position it fixed at 0,0 but behind everything and invisible to the user
  tempDiv.id = 'pdf-gen-container';
  tempDiv.style.position = 'fixed';
  tempDiv.style.top = '0';
  tempDiv.style.left = '0';
  tempDiv.style.width = '800px';
  tempDiv.style.background = 'white';
  tempDiv.style.zIndex = '-99999';
  tempDiv.style.opacity = '0'; // Completely invisible to user
  tempDiv.style.pointerEvents = 'none';
  tempDiv.style.display = 'block';
  tempDiv.style.visibility = 'visible';
  document.body.appendChild(tempDiv);

  try {
    // Wait for content and images to render properly
    await new Promise(resolve => setTimeout(resolve, 1500));

    console.log("Starting canvas capture for high-quality PDF...");

    // Convert to canvas with optimized settings
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false, // Cleaner console
      onclone: (clonedDoc) => {
        const element = clonedDoc.getElementById('pdf-gen-container');
        if (element) {
          // Force visibility in the cloned document for capture
          element.style.opacity = '1';
          element.style.zIndex = '99999';
          element.style.position = 'relative';
          element.style.left = '0';
          element.style.top = '0';
          element.style.visibility = 'visible';
          element.style.display = 'block';
        }
      }
    });

    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgData = canvas.toDataURL('image/jpeg', 0.98); // High quality JPEG
    
    const pdfWidth = 210; 
    const pdfHeight = 297; 
    const imgWidth = pdfWidth - 20; 
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    let heightLeft = imgHeight;
    let position = 10; 

    // Add image to first page
    pdf.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight);
    heightLeft -= (pdfHeight - 20); 

    // Add additional pages if needed
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight);
      heightLeft -= (pdfHeight - 20);
    }

    const filename = `${title.toLowerCase()}-${documentId}.pdf`;
    pdf.save(filename);
    
    console.log("PDF generated successfully:", filename);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw error;
  } finally {
    if (document.body.contains(tempDiv)) {
      document.body.removeChild(tempDiv);
    }
  }
};

// React component (not used in current implementation but kept for potential future use)
const InvoiceTemplate = ({ invoice, user, cartList, type }) => {
  return <div>PDF Template Component</div>;
};

export default InvoiceTemplate;
